import { cancelUpload, uploadFile } from '@weblite/file'
import * as R from 'ramda'
import { MessageType, RawVideoMessage } from '@weblite/ts-action/message'
import { pendingUploadsStorage } from '$logic/pending-uploads/storage'
import {
  PendingUpload,
  PendingUploadVideoAttachment,
  SendMessagePayloadWithPendingId,
} from '$logic/pending-uploads/types'
import { sendMessage } from '$logic/message'
import {
  deleteUploadingFile,
  getUploadingFile,
  saveUploadingFile,
} from '$logic/persist-uploads'
import {
  messageTypeToSendingMessageAction,
  onSendingMessageAction,
} from '$logic/message-action'
import { onAfterMessagesSentToChat } from '$logic/opened-room'
import { getThumbnailUploadId } from './helper'

export function deletePendingUploads(ids: string[]) {
  pendingUploadsStorage.deleteMany(ids)
}

export function deletePendingUpload(uploadId: string) {
  pendingUploadsStorage.delete(uploadId)
  cancelUpload(uploadId)
  cancelUpload(getThumbnailUploadId(uploadId))
  deleteUploadingFile(uploadId)
  deleteUploadingFile(getThumbnailUploadId(uploadId))
}

export async function createPendingUpload(
  message: PendingUpload,
  file: File,
  thumbnailFile?: File,
) {
  if (message.type === 'IMAGE') await saveUploadingFile(message.uploadId, file)
  else saveUploadingFile(message.uploadId, file)

  if (thumbnailFile && message.thumbnailUploadId)
    await saveUploadingFile(message.thumbnailUploadId, thumbnailFile)

  onAfterMessagesSentToChat()
  pendingUploadsStorage.insert(message)

  handlePendingUpload(message, file, thumbnailFile)
}

function convertPendingToSendPayload(
  message: PendingUpload,
  fileId: string,
  size: number,
) {
  const { attachment, type, repliedTo, body, id } = message
  return {
    attachment: { ...attachment, fileId, size },
    type,
    repliedTo,
    body,
    id,
  } as SendMessagePayloadWithPendingId
}

async function processUploadedMessage(
  message: PendingUpload,
  { fileId, size }: { size: number; fileId: string },
) {
  await sendMessage(message.roomId, [
    convertPendingToSendPayload(message, fileId, size),
  ])
}

const processingPendingUploads = new Set<string>()

export function getConversion(
  message: Pick<PendingUpload, 'type'>,
  height?: number,
) {
  switch (message.type) {
    case 'VOICE':
      return { type: 'audio' } as const
    case 'VIDEO':
      return {
        type: 'video',
        options: { format: 'compress', height: height || 360 },
      } as const
    default:
      return undefined
  }
}

async function handleCompleteVideoPendingUpload(uploadId: string) {
  const message = pendingUploadsStorage.get(uploadId)
  const attachment = message.attachment as RawVideoMessage['attachment']
  if (
    attachment.fileId &&
    attachment.thumbnailFileId &&
    message.conversionCompleted
  ) {
    await processUploadedMessage(message, {
      fileId: attachment.fileId,
      size: attachment.size,
    })
    await deleteUploadingFile(message.uploadId)
    await deleteUploadingFile(message.thumbnailUploadId!)
    processingPendingUploads.delete(message.uploadId)
  }
}

async function updatePendingUpload(
  uploadId: string,
  update: (oldMessage: PendingUpload) => PendingUpload,
) {
  const tempMessage = pendingUploadsStorage.get(uploadId)
  const newMessage = update(tempMessage)
  await pendingUploadsStorage.update(newMessage)
}

export function handlePendingUpload(
  message: PendingUpload,
  file: File,
  thumbnailFile?: File,
) {
  processingPendingUploads.add(message.uploadId)
  if (
    thumbnailFile &&
    message.thumbnailUploadId &&
    !(message.attachment as RawVideoMessage['attachment']).thumbnailFileId
  ) {
    uploadFile({
      uploadId: message.thumbnailUploadId,
      file: thumbnailFile,
      type: 'infer',
      onComplete: async info => {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assocPath(
            ['attachment', 'thumbnailFileId'],
            info.fileId,
            oldMessage,
          ),
        )
        handleCompleteVideoPendingUpload(message.uploadId)
      },
    })
  }
  uploadFile({
    uploadId: message.uploadId,
    file,
    type: 'infer',
    async onConversionProgress({ percent }) {
      if (message.thumbnailUploadId && percent === 100) {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assoc('conversionCompleted', true, oldMessage),
        )
        handleCompleteVideoPendingUpload(message.uploadId)
      }
    },
    onComplete: async info => {
      if (message.thumbnailUploadId) {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assoc(
            'attachment',
            {
              ...oldMessage.attachment,
              fileId: info.fileId,
              size: info.size,
            },
            oldMessage,
          ),
        )

        handleCompleteVideoPendingUpload(message.uploadId)
        return
      }
      await processUploadedMessage(message, info)
      await deleteUploadingFile(message.uploadId)
      processingPendingUploads.delete(message.uploadId)
    },
    onProgress() {
      const action =
        messageTypeToSendingMessageAction[message.type as MessageType]
      if (action) onSendingMessageAction(message.roomId, action)
    },
    convert: getConversion(
      message,
      (message.attachment as PendingUploadVideoAttachment).compressionHeight,
    ),
    queue: message.roomId,
  })
}

export async function syncPendingUploads() {
  await Promise.all(
    pendingUploadsStorage.getAll().map(async message => {
      if (processingPendingUploads.has(message.uploadId)) return
      const file = await getUploadingFile(message.uploadId)
      if (!file) return
      if (message.thumbnailUploadId) {
        const thumbnailFile = await getUploadingFile(message.thumbnailUploadId)
        if (thumbnailFile) handlePendingUpload(message, file, thumbnailFile)
      } else handlePendingUpload(message, file)
    }),
  )
}
